import React, { useState, useEffect, useRef } from 'react'
import { useSessionStore } from '../../stores/sessionStore'

const TOOLTIP_DISMISSED_KEY = 'rr:spacebar-tooltip-dismissed'

export function SessionControls() {
  const {
    phase,
    nextAction,
    replayHand,
    nextHand,
    continueAfterRangeUpdate,
  } = useSessionStore()

  const inSession = phase !== 'IDLE' && phase !== 'LOADING_HAND'
  const isShowdown = phase === 'SHOWDOWN'
  const awaitingUpdate = phase === 'AWAITING_RANGE_UPDATE'

  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipDismissed, setTooltipDismissed] = useState(() => {
    try { return localStorage.getItem(TOOLTIP_DISMISSED_KEY) === '1' } catch { return false }
  })
  const nextBtnRef = useRef<HTMLButtonElement>(null)

  // Next Action button acts as Continue when awaiting range update
  const handleNextAction = () => {
    if (awaitingUpdate) {
      continueAfterRangeUpdate()
    } else {
      nextAction()
    }
  }

  // Spacebar triggers the primary action
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return
      if (e.key !== ' ') return

      e.preventDefault()
      const store = useSessionStore.getState()
      if (store.phase === 'SHOWDOWN') {
        store.nextHand()
      } else if (store.phase === 'AWAITING_RANGE_UPDATE') {
        store.continueAfterRangeUpdate()
      } else if (store.phase === 'REPLAYING') {
        store.nextAction()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  const dismissTooltip = () => {
    setTooltipDismissed(true)
    setShowTooltip(false)
    try { localStorage.setItem(TOOLTIP_DISMISSED_KEY, '1') } catch { /* ignore */ }
  }

  return (
    <div className="session-controls">
      {/* Primary: Next action — centered */}
      <div className="ctrl-btn-wrapper">
        <button
          ref={nextBtnRef}
          onClick={isShowdown ? undefined : handleNextAction}
          disabled={!inSession || isShowdown}
          onMouseEnter={() => { if (!tooltipDismissed && !isShowdown) setShowTooltip(true) }}
          onMouseLeave={() => setShowTooltip(false)}
          className="ctrl-btn"
        >
          Next →
        </button>

        {/* Dismissable spacebar tooltip */}
        {showTooltip && !tooltipDismissed && (
          <div className="ctrl-tooltip">
            <div className="ctrl-tooltip-inner">
              <span>Press <kbd className="ctrl-tooltip-kbd">Space</kbd> to continue</span>
              <button
                onClick={(e) => { e.stopPropagation(); dismissTooltip() }}
                className="ctrl-tooltip-dismiss"
                title="Don't show again"
              >✕</button>
            </div>
            <div className="ctrl-tooltip-arrow" />
          </div>
        )}
      </div>

      {/* Showdown-only actions */}
      {isShowdown && (
        <button onClick={replayHand} className="ctrl-btn">
          Replay Hand
        </button>
      )}
      {isShowdown && (
        <button onClick={() => nextHand()} className="ctrl-btn primary">
          Next Hand →
        </button>
      )}
    </div>
  )
}
